import { EApplicantType, EDocumentStatus, EFinancingProgram } from '@if/core/types'
import { UploadAreaButton } from '@if/mui'
import { useUploadRequiredDocument } from '@src/data/api/credit-api/credit-api'
import { Constants } from '@src/types/Constants'
import { ChangeEventHandler, Dispatch, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  applicantType: EApplicantType
  typeId: number
  subKey: string
  creditApplicationId: string
  financingProgramId: EFinancingProgram
  status: EDocumentStatus
  setError: Dispatch<Error>
}

const MAX_FILE_SIZE = 20971520 // 20MB

const UploadFileButton = ({
  applicantType,
  creditApplicationId,
  financingProgramId,
  typeId,
  subKey,
  status,
  setError,
}: Props) => {
  const { t } = useTranslation()

  const [uploadDocument, isLoading] = useUploadRequiredDocument({
    creditApplicationId,
    financingProgramId,
  })

  const onChangeHandler = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      const { files } = event.target
      if (!files) return

      const filesArray = Array.from(files)

      for (const file of filesArray) {
        if (file.size > MAX_FILE_SIZE) {
          setError(new Error(t('common.errors.fileSizeTooBig')))
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }

        const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? ''
        if (!Constants.SupportedDocumentTypes.includes(fileExtension)) {
          setError(
            new Error(
              t('common.errors.unsupportedFileType', {
                supportedTypes: Constants.SupportedDocumentTypes,
              }),
            ),
          )
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }

        if (!Constants.SupportedDocumentMimeTypes.includes(file.type) && !['heif', 'heic'].includes(fileExtension)) {
          setError(
            new Error(
              t('common.errors.unsupportedFileType', {
                supportedTypes: Constants.SupportedDocumentTypes,
              }),
            ),
          )
          window.scrollTo({ top: 0, behavior: 'smooth' })
          return
        }
      }

      await uploadDocument({
        financingProgramId,
        creditApplicationId,
        applicantType,
        typeId,
        subKey,
        files: filesArray,
      })
    },
    [applicantType, creditApplicationId, financingProgramId, setError, subKey, t, typeId, uploadDocument],
  )

  if (status !== EDocumentStatus.Approved) {
    return (
      <UploadAreaButton
        color="primary"
        loading={isLoading}
        accept={Constants.SupportedDocumentTypes}
        onChange={onChangeHandler}
        size="large"
      >
        {t('viewCreditApplication.uploadDocument')}
      </UploadAreaButton>
    )
  }

  return null
}

export default UploadFileButton
